import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { stripLang, withLang } from "../utils/localePath";

import welcomeIcon from "../assets/icons/welcome.svg";
import versionIcon from "../assets/icons/Version.svg";
import tutorialIcon from "../assets/icons/tutorial.svg";
import priceIcon from "../assets/icons/price.svg";
import downloadIcon from "../assets/icons/download.svg";
import caseIcon from "../assets/icons/case.svg";

import arrowDownIcon from "../assets/icons/arrow-down.svg"; // arrow sáng
import arrowDownDark from "../assets/icons/arrow-down-black.svg"; // arrow đậm
import powered from "../assets/icons/powered.svg";
import powereddark from "../assets/icons/powereddark.svg";

const Sidebar = ({ isMobile = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname: fullPathname } = useLocation();
  // So khớp menu theo path nội bộ (bỏ /en|/vi)
  const pathname = stripLang(fullPathname);

  const [openVersion, setOpenVersion] = useState(false);
  const [versionValue, setVersionValue] = useState("v1");

  const [openGroup, setOpenGroup] = useState({
    install: false,
    usage: false,
    cases: false,
  });

  const toggleGroup = (key) => {
    setOpenGroup((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const goto = (path) => {
    navigate(withLang(path));

    // scroll lên đầu ngay sau khi navigate
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 0);
  };

  // ✅ Refs for auto-scroll
  const menuRef = useRef(null);
  const groupBtnRef = useRef({ install: null, usage: null, cases: null });
  const activeItemRef = useRef(null);

  const menuBtnClass = `
    w-full text-left px-3 py-2
    transition-colors
    hover:bg-[#F1F5F9]
    dark:hover:bg-[#334155]
    text-[#334155]
    dark:text-[#F8FAFC]
    data-[active=true]:text-[#0087FE]
    dark:data-[active=true]:text-[#0087FE]
  `;

  const menuBtnClassNone = `
    w-full text-left px-3 py-2
    transition-colors
    hover:bg-[#F1F5F9]
    dark:hover:bg-[#334155]
    text-[#334155]
    dark:text-[#F8FAFC]
    data-[active=true]:text-[#0087FE]
    dark:data-[active=true]:text-[#0087FE]
  `;

  const versionWrapRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!openVersion) return;
      if (versionWrapRef.current && !versionWrapRef.current.contains(e.target)) {
        setOpenVersion(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openVersion]);


  // ✅ Route match helpers
  const isInstall =
    pathname === "/installation" ||
    pathname.startsWith("/install-gopaperlessagent") ||
    pathname.startsWith("/uninstall-gopaperlessagent") ||
    pathname.startsWith("/guide-to-adding-and-trusting-digital-certificates-from-cas-on-macos");

  const isUsage =
    pathname === "/userguide" ||
    pathname.startsWith("/login-gopaperlessagent") ||
    pathname.startsWith("/view-cert") ||
    pathname.startsWith("/message-box") ||
    pathname.startsWith("/supported-sign");

  const isCases = pathname === "/usecase" || pathname.startsWith("/cases/");

  // ✅ Auto-open group theo pathname (chỉ mở thêm, không tắt cái đang mở)
  useEffect(() => {
    setOpenGroup((prev) => ({
      install: prev.install || isInstall,
      usage: prev.usage || isUsage,
      cases: prev.cases || isCases,
    }));
  }, [isInstall, isUsage, isCases]);

  // ✅ Auto-scroll tới item active (ưu tiên), nếu không có thì tới group
  useEffect(() => {
    const scrollContainer = menuRef.current;
    if (!scrollContainer) return;

    const key = isInstall ? "install" : isUsage ? "usage" : isCases ? "cases" : null;

    // đợi DOM render xong (đặc biệt khi group vừa mở)
    requestAnimationFrame(() => {
      const targetEl =
        activeItemRef.current || (key ? groupBtnRef.current[key] : null);

      targetEl?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });
  }, [pathname, isInstall, isUsage, isCases, openGroup.install, openGroup.usage, openGroup.cases]);

  return (
    <div
      className={`flex flex-col justify-between h-full ${isMobile ? "w-full " : "w-[300px]"
        } bg-transparent`}
    >
      <div>
        {/* Version select */}
        <div className="py-3 desktop:mb-[5%]">
          <div ref={versionWrapRef} className="relative">
            <button
              type="button"
              onClick={() => setOpenVersion((v) => !v)}
              className="
        w-full border border-[#94A3B8] dark:border-[#475569]
        text-[16px] text-[#334155] dark:text-[#E2E8F0]
        bg-transparent px-4 h-[48px]
        rounded-md flex items-center justify-between
      "
            >
              {versionValue}
              <span
                className={`
          transition-transform duration-200
          ${openVersion ? "rotate-180" : "rotate-0"}
        `}
              >
                <img src={arrowDownDark} className="w-3 h-3 dark:hidden" alt="arrow down" />
                <img src={arrowDownIcon} className="w-3 h-3 hidden dark:block" alt="arrow down" />
              </span>
            </button>

            {openVersion && (
              <div className="absolute left-0 top-[56px] w-full rounded-md dark:bg-neutral-10 bg-white shadow-md z-50">
                <button
                  type="button"
                  onClick={() => {
                    setVersionValue("v1");
                    setOpenVersion(false);
                  }}
                  className="
            w-full px-3 py-2 text-sm text-left text-[#334155] dark:text-[#E2E8F0]
            hover:bg-[#346DDB] hover:rounded-md hover:text-[#FFFFFF] h-[48px]
          "
                >
                  v1
                </button>
              </div>
            )}
          </div>
        </div>

        {/* MENU */}
        <div
          ref={menuRef}
          className="
            flex-1 overflow-y-auto scroll-thin max-h-[70vh]
            bg-[#E2E8F0] dark:bg-[#0F172A] rounded-md p-[8px]
          "
        >
          {/* WELCOME */}
          <button
            className={menuBtnClass}
            data-active={pathname === "/"}
            onClick={() => goto("/")}
          >
            <div className="flex items-center gap-2">
              <img src={welcomeIcon} alt="welcome" className="w-6 h-6" />
              <span className="text-[16px] font-medium">{t("sidebar.welcome")}</span>
            </div>
          </button>

          {/* VERSION */}
          <button
            className={menuBtnClass}
            data-active={pathname === "/version"}
            onClick={() => goto("/version")}
          >
            <div className="flex items-center gap-2">
              <img src={versionIcon} alt="version" className="w-6 h-6" />
              <span className="text-[16px] font-medium">{t("sidebar.version")}</span>
            </div>
          </button>

          {/* INSTALL GROUP */}
          <button
            ref={(el) => (groupBtnRef.current.install = el)}
            className={menuBtnClassNone}
            data-active={pathname === "/installation"}
            onClick={() => {
              if (pathname === "/installation") {
                toggleGroup("install"); // ✅ đang ở trang → mới toggle
              } else {
                goto("/installation"); // ✅ lần đầu → chỉ goto
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={downloadIcon} alt="download" className="w-6 h-6" />
                <span className="text-[16px] font-medium">
                  {t("sidebar.install.title")}
                </span>
              </div>

              <img
                src={arrowDownDark}
                alt="arrow-dark"
                className={`w-3 h-3 dark:hidden transition-transform ${openGroup.install ? "rotate-0" : "-rotate-90"
                  }`}
              />
              <img
                src={arrowDownIcon}
                alt="arrow-light"
                className={`w-3 h-3 hidden dark:block transition-transform ${openGroup.install ? "rotate-0" : "-rotate-90"
                  }`}
              />
            </div>
          </button>

          {openGroup.install && (
            <ul className="pl-5 text-[16px] pb-2">
              <li
                ref={pathname === "/install-gopaperlessagent" ? activeItemRef : null}
                onClick={() => goto("/install-gopaperlessagent")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/install-gopaperlessagent"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.install.itemInstall")}
              </li>

              <li
                ref={pathname === "/uninstall-gopaperlessagent" ? activeItemRef : null}
                onClick={() => goto("/uninstall-gopaperlessagent")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/uninstall-gopaperlessagent"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.install.itemUninstall")}
              </li>
              <li
                ref={pathname === "/guide-to-adding-and-trusting-digital-certificates-from-cas-on-macos" ? activeItemRef : null}
                onClick={() => goto("/guide-to-adding-and-trusting-digital-certificates-from-cas-on-macos")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/guide-to-adding-and-trusting-digital-certificates-from-cas-on-macos"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.install.itemGuideToAddingandTrustingDigitalCertificatesFromCAsOnMacOS")}
              </li>
            </ul>
          )}

          {/* USAGE GROUP */}
          <button
            ref={(el) => (groupBtnRef.current.usage = el)}
            className={menuBtnClassNone}
            data-active={pathname === "/userguide"}
            onClick={() => {
              if (pathname === "/userguide") {
                toggleGroup("usage");
              } else {
                goto("/userguide");
              }
            }}

          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={tutorialIcon} alt="tutorial" className="w-6 h-6" />
                <span className="text-[16px] font-medium">
                  {t("sidebar.usage.title")}
                </span>
              </div>

              <img
                src={arrowDownDark}
                alt="arrow-dark"
                className={`w-3 h-3 dark:hidden transition-transform ${openGroup.usage ? "rotate-0" : "-rotate-90"
                  }`}
              />
              <img
                src={arrowDownIcon}
                alt="arrow-light"
                className={`w-3 h-3 hidden dark:block transition-transform ${openGroup.usage ? "rotate-0" : "-rotate-90"
                  }`}
              />
            </div>
          </button>

          {openGroup.usage && (
            <ul className="pl-5 text-[16px] pb-2">
              {/* <li
                ref={pathname === "/config-gopaperlessagent" ? activeItemRef : null}
                onClick={() => goto("/config-gopaperlessagent")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/config-gopaperlessagent"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.usage.config")}
              </li> */}

              <li
                ref={pathname === "/login-gopaperlessagent" ? activeItemRef : null}
                onClick={() => goto("/login-gopaperlessagent")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/login-gopaperlessagent"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.usage.login")}
              </li>

              <li
                ref={pathname === "/view-cert" ? activeItemRef : null}
                onClick={() => goto("/view-cert")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/view-cert"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.usage.viewCert")}
              </li>

              <li
                ref={pathname === "/message-box" ? activeItemRef : null}
                onClick={() => goto("/message-box")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/message-box"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.usage.messageBox")}
              </li>

              <li
                ref={pathname === "/supported-sign" ? activeItemRef : null}
                onClick={() => goto("/supported-sign")}
                className={`
                  cursor-pointer py-2 pl-2 border-l border-[#CBD5E1]
                  hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  ${pathname === "/supported-sign"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.usage.supportedSign")}
              </li>
            </ul>
          )}

          {/* CASES GROUP */}
          <button
            ref={(el) => (groupBtnRef.current.cases = el)}
            className={menuBtnClassNone}
            data-active={pathname === "/usecase"}
            onClick={() => {
              if (pathname === "/usecase") {
                toggleGroup("cases");
              } else {
                goto("/usecase");
              }
            }}

          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={caseIcon} alt="case" className="w-6 h-6" />
                <span className="text-[16px] font-medium">
                  {t("sidebar.cases.title")}
                </span>
              </div>

              <img
                src={arrowDownDark}
                alt="arrow-dark"
                className={`w-3 h-3 dark:hidden transition-transform ${openGroup.cases ? "rotate-0" : "-rotate-90"
                  }`}
              />
              <img
                src={arrowDownIcon}
                alt="arrow-light"
                className={`w-3 h-3 hidden dark:block transition-transform ${openGroup.cases ? "rotate-0" : "-rotate-90"
                  }`}
              />
            </div>
          </button>

          {openGroup.cases && (
            <ul className="pl-5 text-[16px] pb-2">
              <li
                ref={pathname === "/cases/word" ? activeItemRef : null}
                onClick={() => goto("/cases/word")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/word"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.word")}
              </li>

              <li
                ref={pathname === "/cases/excel" ? activeItemRef : null}
                onClick={() => goto("/cases/excel")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/excel"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.excel")}
              </li>

              <li
                ref={pathname === "/cases/powerpoint" ? activeItemRef : null}
                onClick={() => goto("/cases/powerpoint")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/powerpoint"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.powerPoint")}
              </li>

              {/* <li
                ref={pathname === "/cases/outlook" ? activeItemRef : null}
                onClick={() => goto("/cases/outlook")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/outlook"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.outlook")}
              </li> */}

              <li
                ref={pathname === "/cases/foxit" ? activeItemRef : null}
                onClick={() => goto("/cases/foxit")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/foxit"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.foxit")}
              </li>

              {/* <li
                ref={pathname === "/cases/pdf-sign" ? activeItemRef : null}
                onClick={() => goto("/cases/pdf-sign")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/pdf-sign"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.pdfSign")}
              </li> */}

              <li
                ref={pathname === "/cases/acrobat-reader" ? activeItemRef : null}
                onClick={() => goto("/cases/acrobat-reader")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/acrobat-reader"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.acrobatReader")}
              </li>

              {/* <li
                ref={pathname === "/cases/acrobat-writer" ? activeItemRef : null}
                onClick={() => goto("/cases/acrobat-writer")}
                className={`
                  cursor-pointer hover:bg-[#F1F5F9] dark:hover:bg-[#334155]
                  py-2 border-l border-[#CBD5E1] pl-2
                  ${pathname === "/cases/acrobat-writer"
                    ? "text-[#0087FE] border-l-[#0087FE]"
                    : "text-[#334155] dark:text-[#E2E8F0] border-[#CBD5E1] dark:border-[#334155]"
                  }
                `}
              >
                {t("sidebar.cases.acrobatWriter")}
              </li> */}
            </ul>
          )}

          {/* PRICING */}
          <button
            className={menuBtnClass}
            data-active={pathname === "/pricing"}
            onClick={() => goto("/pricing")}
          >
            <div className="flex items-center gap-2">
              <img src={priceIcon} alt="price" className="w-6 h-6" />
              <span className="text-[16px] font-medium">{t("sidebar.pricing")}</span>
            </div>
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="desktop:pb-[10%] tabletpro:mt-[1%] tabletair:mt-[2%] tabletmini:mt-[2%] mobile:mt-[3%] mini_mobile:mt-[3%]">
        <div
          className="
            w-full text-center text-[#6B7280]
            py-2
            bg-[#0000001A] dark:bg-[#FFFFFF1A]
            hover:bg-[#0000001A]/20 dark:hover:bg-white/20
            backdrop-blur-[16px]
          "
          style={{
            boxShadow: `
              0px 6px 12px 0px #FFFFFF1F inset,
              0px 1px 1px 0px #FFFFFF33 inset
            `,
          }}
        >
          <a
            href="https://mobile-id.vn/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="flex items-center justify-center gap-2">
              <img src={powered} alt="powered" className="block dark:hidden w-4 h-4 " />
              <img src={powereddark} alt="powered-dark" className="hidden dark:block w-4 h-4" />
              <span className="text-[16px] text-[#020617] dark:text-[#F8FAFC]">
                {t("footer.powered")}
              </span>
            </div>
          </a>

        </div>
      </div>
    </div>
  );
};

export default Sidebar;
